import { STOCK_RESOURCES, type ResourceAmounts, type StockResource } from '../buildings/definitions';

export type Stock = Record<StockResource, number>;

export interface Affordability {
  readonly ok: boolean;
  /** How much more of each resource is needed (only resources that are short). */
  readonly missing: ResourceAmounts;
}

/**
 * One player's resources. Whole numbers only, never negative, never above
 * capacity. Every change goes through a method that checks first, so an
 * invalid transaction can never leave the ledger half-applied.
 * Engine-free and JSON-serialisable (`toJSON`).
 */
export class ResourceLedger {
  private readonly amounts: Stock;
  private cap: Stock;

  constructor(start: ResourceAmounts, capacity: ResourceAmounts) {
    this.cap = toStock(capacity);
    this.amounts = toStock(start);
    for (const r of STOCK_RESOURCES) this.amounts[r] = Math.min(this.amounts[r], this.cap[r]);
  }

  get(r: StockResource): number {
    return this.amounts[r];
  }

  capacity(r: StockResource): number {
    return this.cap[r];
  }

  /** Free storage for a resource. */
  space(r: StockResource): number {
    return this.cap[r] - this.amounts[r];
  }

  canAfford(cost: ResourceAmounts): Affordability {
    const missing: Partial<Record<StockResource, number>> = {};
    let ok = true;
    for (const r of STOCK_RESOURCES) {
      const need = cost[r] ?? 0;
      if (need > this.amounts[r]) {
        missing[r] = need - this.amounts[r];
        ok = false;
      }
    }
    return { ok, missing };
  }

  /** Deduct a cost. Throws (changing nothing) if it cannot be afforded — callers validate first. */
  spend(cost: ResourceAmounts): void {
    assertWhole(cost);
    if (!this.canAfford(cost).ok) throw new Error('Cannot afford cost');
    for (const r of STOCK_RESOURCES) this.amounts[r] -= cost[r] ?? 0;
  }

  /** Add up to the free space. Returns how much was stored and how much did not fit. */
  add(r: StockResource, amount: number): { stored: number; overflow: number } {
    assertWhole({ [r]: amount });
    const stored = Math.min(amount, this.space(r));
    this.amounts[r] += stored;
    return { stored, overflow: amount - stored };
  }

  /** Remove up to `amount` (never below zero). Returns how much was actually removed. */
  take(r: StockResource, amount: number): number {
    assertWhole({ [r]: amount });
    const taken = Math.min(amount, this.amounts[r]);
    this.amounts[r] -= taken;
    return taken;
  }

  /** Change storage capacity. Anything above the new capacity is lost; returns what was lost. */
  setCapacity(capacity: ResourceAmounts): ResourceAmounts {
    this.cap = toStock(capacity);
    const lost: Partial<Record<StockResource, number>> = {};
    for (const r of STOCK_RESOURCES) {
      if (this.amounts[r] > this.cap[r]) {
        lost[r] = this.amounts[r] - this.cap[r];
        this.amounts[r] = this.cap[r];
      }
    }
    return lost;
  }

  toJSON(): { amounts: Stock; capacity: Stock } {
    return { amounts: { ...this.amounts }, capacity: { ...this.cap } };
  }
}

export function toStock(a: ResourceAmounts): Stock {
  return { wood: a.wood ?? 0, scrap: a.scrap ?? 0, food: a.food ?? 0 };
}

export function isEmptyAmounts(a: ResourceAmounts): boolean {
  return STOCK_RESOURCES.every((r) => (a[r] ?? 0) === 0);
}

function assertWhole(a: ResourceAmounts): void {
  for (const r of STOCK_RESOURCES) {
    const v = a[r];
    if (v !== undefined && (!Number.isInteger(v) || v < 0)) throw new Error(`Resource amounts must be whole and ≥ 0 (${r}: ${v})`);
  }
}
